const boundCards = new WeakSet();
const cardStates = new WeakMap();
const autonomousCards = new Set();
const LERP_FACTOR = 0.16;
const CARD_SELECTOR = ".c-cta-card, .c-article-card, .c-resource-card";
const HERO_SELECTOR = ".c-page-hero";
let autonomousRafId = 0;
let autonomousStartTime = 0;
let hasVisibilityListener = false;
let heroPointerMoveHandler = null;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function ensureState(card) {
  const existing = cardStates.get(card);
  if (existing) return existing;

  const state = {
    currentX: 50,
    currentY: 50,
    targetX: 50,
    targetY: 50,
    rafId: 0,
    isPointerInside: false,
    /** When true, never strip --cta-glow-* after settling at center (e.g. page hero). */
    persistGlow: false,
    seed: Math.random() * Math.PI * 2,
    radiusX: 14 + Math.random() * 10,
    radiusY: 10 + Math.random() * 8,
  };
  cardStates.set(card, state);
  return state;
}

function applyGlow(card, state) {
  card.style.setProperty("--cta-glow-x", `${state.currentX.toFixed(2)}%`);
  card.style.setProperty("--cta-glow-y", `${state.currentY.toFixed(2)}%`);
}

function animate(card, state) {
  state.currentX += (state.targetX - state.currentX) * LERP_FACTOR;
  state.currentY += (state.targetY - state.currentY) * LERP_FACTOR;
  applyGlow(card, state);

  const dx = Math.abs(state.targetX - state.currentX);
  const dy = Math.abs(state.targetY - state.currentY);
  const isSettled = dx < 0.05 && dy < 0.05;

  if (isSettled) {
    state.rafId = 0;

    if (
      !state.persistGlow &&
      !state.isPointerInside &&
      state.targetX === 50 &&
      state.targetY === 50
    ) {
      card.style.removeProperty("--cta-glow-x");
      card.style.removeProperty("--cta-glow-y");
    }
    return;
  }

  state.rafId = window.requestAnimationFrame(() => animate(card, state));
}

function queueAnimation(card, state) {
  if (state.rafId) return;
  state.rafId = window.requestAnimationFrame(() => animate(card, state));
}

function setGlowPosition(card, event) {
  const state = ensureState(card);
  const rect = card.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;

  state.targetX = clamp(x, 0, 100);
  state.targetY = clamp(y, 0, 100);
  queueAnimation(card, state);
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function animateAutonomousGlow(timestamp) {
  if (!autonomousStartTime) autonomousStartTime = timestamp;
  const seconds = (timestamp - autonomousStartTime) / 1000;

  autonomousCards.forEach((card) => {
    if (!(card instanceof HTMLElement) || !card.isConnected) {
      autonomousCards.delete(card);
      return;
    }

    const state = ensureState(card);
    state.isPointerInside = false;

    const waveX = Math.sin(seconds * 0.11 + state.seed) * state.radiusX;
    const waveY = Math.cos(seconds * 0.085 + state.seed * 1.3) * state.radiusY;
    const driftX = Math.sin(seconds * 0.037 + state.seed * 2.1) * 4;
    const driftY = Math.cos(seconds * 0.043 + state.seed * 1.8) * 3;

    state.targetX = clamp(50 + waveX + driftX, 10, 90);
    state.targetY = clamp(50 + waveY + driftY, 10, 90);
    queueAnimation(card, state);
  });

  if (autonomousCards.size === 0 || prefersReducedMotion()) {
    autonomousRafId = 0;
    return;
  }

  autonomousRafId = window.requestAnimationFrame(animateAutonomousGlow);
}

function startAutonomousGlow() {
  if (autonomousRafId || prefersReducedMotion()) return;
  autonomousStartTime = 0;
  autonomousRafId = window.requestAnimationFrame(animateAutonomousGlow);
}

export function initCtaCardGlow() {
  if (heroPointerMoveHandler) {
    document.removeEventListener("pointermove", heroPointerMoveHandler);
    heroPointerMoveHandler = null;
  }

  const supportsFineHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const reduceMotion = prefersReducedMotion();

  const cards = document.querySelectorAll(CARD_SELECTOR);

  cards.forEach((card) => {
    if (!(card instanceof HTMLElement) || boundCards.has(card)) return;
    boundCards.add(card);

    if (supportsFineHover) {
      card.classList.remove("is-auto-glow");
      card.addEventListener("pointerenter", () => {
        const state = ensureState(card);
        state.isPointerInside = true;
        queueAnimation(card, state);
      });

      card.addEventListener("pointermove", (event) => {
        if (!(event instanceof PointerEvent)) return;
        setGlowPosition(card, event);
      });

      card.addEventListener("pointerleave", () => {
        const state = ensureState(card);
        state.isPointerInside = false;
        state.targetX = 50;
        state.targetY = 50;
        queueAnimation(card, state);
      });
      return;
    }

    card.classList.add("is-auto-glow");
    autonomousCards.add(card);

    // Set initial glow position before autonomous animation loop starts.
    const state = ensureState(card);
    applyGlow(card, state);
  });

  const heroes = Array.from(document.querySelectorAll(HERO_SELECTOR)).filter(
    (el) => el instanceof HTMLElement && !boundCards.has(el)
  );

  heroes.forEach((hero) => {
    if (!(hero instanceof HTMLElement)) return;
    boundCards.add(hero);
    const state = ensureState(hero);

    if (supportsFineHover && !reduceMotion) {
      state.persistGlow = true;
      state.isPointerInside = true;
      hero.classList.remove("is-auto-glow");
      return;
    }

    if (supportsFineHover && reduceMotion) {
      state.persistGlow = true;
      state.isPointerInside = true;
      state.targetX = state.currentX = 50;
      state.targetY = state.currentY = 50;
      hero.classList.remove("is-auto-glow");
      applyGlow(hero, state);
      return;
    }

    state.persistGlow = false;
    hero.classList.add("is-auto-glow");
    autonomousCards.add(hero);
    applyGlow(hero, state);
  });

  if (supportsFineHover && !reduceMotion && heroes.length > 0) {
    heroPointerMoveHandler = (event) => {
      if (typeof event.clientX !== "number" || typeof event.clientY !== "number") return;
      heroes.forEach((hero) => {
        if (!(hero instanceof HTMLElement) || !hero.isConnected) return;
        const st = ensureState(hero);
        st.isPointerInside = true;
        setGlowPosition(hero, event);
      });
    };
    document.addEventListener("pointermove", heroPointerMoveHandler, { passive: true });
    heroPointerMoveHandler({
      clientX: window.innerWidth / 2,
      clientY: window.innerHeight / 2,
    });
  }

  if (!supportsFineHover) {
    startAutonomousGlow();
    if (!hasVisibilityListener) {
      hasVisibilityListener = true;
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) return;
        autonomousCards.forEach((card) => {
          if (!(card instanceof HTMLElement) || !card.isConnected) return;
          const state = ensureState(card);
          applyGlow(card, state);
        });
        startAutonomousGlow();
      });
    }
  }
}
